<!-- eslint-disable no-unused-vars -->
<template>
  <svg
    :id="id"
    ref="svgElement"
    class="line-chart simcision-chart"
    :style="{ fontSize: (settings.fontSize || 12) + 'px' }"
    :key="id"
  ></svg>
</template>

<script setup lang="ts">
import { v4 } from "uuid";
import { ref, computed, reactive, onMounted, watch } from "vue";
import { cloneDeep } from "lodash-es";
import { line } from "d3-shape";
import { axisBottom } from "d3-axis";
import { select } from "d3-selection";
import { scaleLinear, scalePoint } from "d3-scale";
import { useI18n } from "vue-i18n";

import { getVerticallySpacedLabelYs } from "./spaceLabels";
import {
  drawColorScale,
  drawVerticalYAxis,
  addRoundActionSymbols,
  selectOrCreateChild
} from "./chartDrawFunction";
import { useChartEngine } from "./useChartEngine";

import type { ScaleLinear } from "d3-scale";
import type { Selection } from "d3-selection";
import type { ChartComponentProps, ChartEmitEvents } from "./useChartEngine";
import type { BaseType } from "d3-selection";
import type { ColumnEntry, DataEntry, EntryValue } from "./ChartData";
import { captureException } from "@sentry/vue";

const { t } = useI18n();

const props = withDefaults(defineProps<ChartComponentProps>(), {
  id: () => v4(),
  yUnit: "%",
  width: 960,
  height: 500,
  settings: () => ({}),
  formattedTitle: () => [],
  animationsDisabled: false,
  titleClickable: false,
  margin: () => ({ top: 20, left: 20, bottom: 20, right: 20 })
});

const emit = defineEmits<ChartEmitEvents>();

const prePatchedProps = computed(() => ({
  ...props,

  // Extra data:
  // add an extra entry for the previous value
  // of the first value
  data: props.data.map((d) => {
    if (d.values.length == 0) return d;
    const extraFirstValue = cloneDeep(d.values[0]);
    extraFirstValue.value = extraFirstValue.startValue || 0;
    const keyparts = extraFirstValue.key.split("-");
    extraFirstValue.key = keyparts[0] + "-" + (parseInt(keyparts[1]) - 1);
    extraFirstValue.columnId = "start";
    extraFirstValue.title = "start";
    extraFirstValue.shortTitle = "start";
    return {
      ...d,
      values: [extraFirstValue, ...d.values]
    };
  })
}));

const ctx = useChartEngine(prePatchedProps, { emit });
// @ts-expect-error - svgElement is used as template ref
const { svgElement } = ctx; // Extracted for template ref binding (used in <svg ref="svgElement">)
const chart = reactive(ctx);

const yScaleByUnit = ref<{
  [unit: string]: ScaleLinear<number, number, never>;
}>({
  "%": scaleLinear()
});
const xScale = ref(scalePoint());

const OUTER_PADDING = computed(() => {
  const fontSize = props.settings.fontSize || 12;
  return (props.settings.yAxisTitleVisible ? fontSize : 0) + (props.settings.yAxisLabelsVisible ? fontSize / 2 : 0) + 10;
});

const resolvedColumns = computed(() => {
  return (
    props.columnData ||
    chart.longestValuesArray.reduce((entries, v) => {
      entries.set(v.columnId, {
        id: v.id,
        key: v.key,
        type: v.type,
        title: v.title,
        shortTitle: v.shortTitle,
        values: []
      });
      return entries;
    }, new Map<string, ColumnEntry>())
  );
});

const labelsRotated = computed(() => {
  const longestXLabel = [...resolvedColumns.value.values()].reduce(
    (longest, columnEntry) => {
      const title = props.settings.xAxisLabelsShortened
        ? columnEntry?.shortTitle
        : columnEntry?.title;
      return Math.max(title.length || 0, longest);
    },
    0
  );
  return longestXLabel >= 3;
});

const roundEntriesHeight = computed(() => {
  if (!props.settings.roundEffectsVisible) return 0;
  const columns = resolvedColumns.value;

  // Add height for round effects
  const columnRows =
    columns == null
      ? new Set<never>()
      : [...columns.values()].reduce((columnRowKeys, columnDataEntry) => {
          const values = columnDataEntry.values || [];
          for (const value of values) {
            if (!columnRowKeys.has(value.id)) columnRowKeys.add(value.id);
          }
          return columnRowKeys;
        }, new Set<string | number>());
  return columnRows.size * 16;
});

watch(
  [() => props.data, () => props.settings],
  () => {
    updateChart();
  },
  { deep: true }
);

watch([() => props.width, () => props.height], ([width, height]) => {
  if (!chart.svg) return;
  chart.svg.attr("width", width);
  chart.svg.attr("height", height);
  chart.svg
    .select(".chart__background")
    .attr("width", width)
    .attr("height", height);
  updateChart();
});

onMounted(() => {
  createChart();

  if (!props.animationsDisabled) {
    setTimeout(() => {
      chart.animationDuration = 500;
    }, 200);
  }
});

// ===============
// METHODS

/**
 * Setup the chart.
 * This needs to be called before any update functions.
 */
function createChart() {
  if (!chart.svgElement) return;

  const svg = select<SVGSVGElement, unknown>(chart.svgElement)
    .attr("width", props.width)
    .attr("height", props.height);

  // Create the background rect
  // This is relevant for SVG exports.
  if (props.settings?.backgroundFilled !== false) {
    svg
      .append("rect")
      .attr("width", props.width)
      .attr("height", props.height)
      .attr("class", "chart__background");
  }

  // (Re-)Create the container
  const g = svg.append("g").attr("class", "chart__container");

  const header = svg.append("g").attr("class", "chart__header");
  header.append("rect").attr("class", "chart__header-bg");
  header.append("text").attr("class", "chart__title");

  // create the axis containers
  g.append("g").attr("class", "gx");
  g.append("g").attr("class", "gy");
  g.append("g").attr("class", "chart-colorscale");
  g.append("g").attr("class", "chart-groups");

  // save the svg and container to the component data.
  chart.svg = svg;
  chart.container = g;

  updateChart();
}

/**
 * Update the SVG: change it to represent the
 * current state of the data array.
 */
function updateChart() {
  updateLabelsHeight();
  updateLabelsWidth();
  updatePaddings();
  positionContainer();

  chart.drawTitle();

  updateScales();

  drawAxes();
  drawScaleColors();

  drawLines();

  drawXAxisLabel();
  drawYAxisLabels();
}

function positionContainer() {
  if (!chart.container) return;

  chart.container
    .transition()
    .duration(chart.animationDuration)
    .attr(
      "transform",
      `translate(${props.margin.left + chart.paddingLeft}, ${
        props.margin.top + chart.titleHeight
      })`
    );
}

function updateLabelsHeight() {
  let xAxisTickHeight = (props.settings.fontSize || 12) + 1;

  const columns = resolvedColumns.value;

  if (labelsRotated.value) {
    let longestWidth = 0;

    for (const [columnId] of columns) {
      const labelText = props.settings.xAxisLabelsShortened
        ? columns.get(columnId)?.shortTitle || ""
        : columns.get(columnId)?.title || "";
      const textLength = chart.measureTextWidthCached(labelText, (props.settings.fontSize || 12) - 1);
      longestWidth = Math.max(longestWidth, textLength);
    }

    const hypothenuse = Math.round(longestWidth);

    xAxisTickHeight = Math.round(Math.sqrt(Math.pow(hypothenuse, 2) / 2)) + 5;
  }

  chart.labelsHeight = xAxisTickHeight + roundEntriesHeight.value;
}
function updateLabelsWidth() {
  if (!props.settings.groupLabelsVisible) {
    chart.labelsWidth = 0;
  } else {
    let longestWidth = 0;

    for (const d of chart.filteredData) {
      const title = getLabelTitle(d);

      // save the longest width
      const width = chart.measureTextWidthCached(title, (props.settings.fontSize || 12) + 2);
      longestWidth = Math.max(longestWidth, width);
    }

    chart.labelsWidth = Math.round(longestWidth) + 25;
  }
}
function updatePaddings() {
  chart.paddingLeft = chart.yTicksWidth;

  if (props.settings.scaleColorsVisible) {
    const percentUnitIndex = chart.yUnits.indexOf("%");
    if (percentUnitIndex >= 0) {
      chart.paddingLeft += 12;
    }
  }

  // action x axis label width
  let xAxisTitleWidth = 0;
  if (props.settings.xAxisTitleVisible) {
    const xAxisLabelString = props.settings.xAxis
      ? t(`chartAxes.${props.settings.xAxis}`)
      : "";

    xAxisTitleWidth = chart.measureTextWidthCached(xAxisLabelString, props.settings.fontSize || 12);
  }

  chart.paddingRight =
    Math.max(chart.labelsWidth, xAxisTitleWidth) - OUTER_PADDING.value + 15;
}

/**
 * Update xScale and yScales, depending on the data and the chart dimensions.
 */
function updateScales() {
  updateXScale();
  updateYScale();
}
function updateXScale() {
  const domain = chart.longestValuesArray.map((v) => v.columnId);
  xScale.value.domain(domain).range([0, chart.innerWidth]).align(0.5);

  // Apply an outer padding to the x scale
  // The padding is set as a fraction of the step size (0 - 1),
  // so we divide by the step size to get an absolute value.
  const step = xScale.value.step();
  xScale.value.padding(OUTER_PADDING.value / step);
}
function updateYScale() {
  for (const unit of chart.yUnits) {
    if (yScaleByUnit.value[unit] == null) {
      yScaleByUnit.value[unit] = scaleLinear();
    }

    const unitDomain = chart.unitDomains[unit];

    // update y scale
    yScaleByUnit.value[unit].domain(unitDomain).range([chart.innerHeight, 0]);
  }
}

/**
 * Draw X and Y axes.
 */
function drawAxes() {
  const g = chart.container;
  const scaleX = xScale.value;

  if (!g) return;

  drawVerticalYAxis(chart.yUnits, g.select(".gy"), yScaleByUnit.value, {
    ...props.settings,
    unitColors: chart.unitColors,
    width: chart.innerWidth,
    animationDuration: chart.animationDuration,
    yUnitWidths: chart.yUnitWidths
  });

  // === X AXIS ===
  const columns = resolvedColumns.value;

  const columnKeys = columns ? [...columns.keys()] : [];

  const columnWidth = scaleX.step();

  function getColumnTitle(columnKey: string) {
    if (!columns?.has(columnKey)) return columnKey;
    return props.settings.xAxisLabelsShortened
      ? columns.get(columnKey)?.shortTitle || ""
      : columns.get(columnKey)?.title || "";
  }

  const longestTitleLength = columnKeys.reduce((longest, columnId) => {
    const title = getColumnTitle(columnId);
    return Math.max(title.length || 0, longest);
  }, 0);

  // if the column width is too small, only show every xth column
  const minColumnWidth = (longestTitleLength + 0.5) * (props.settings.fontSize || 12);
  const minColumnWidthIndex = Math.max(Math.floor(minColumnWidth / columnWidth), 1);
  const displayedColumnKeys = columnKeys.filter((_, i) => {
    if (i == 0) return true; // show the first column
    const lastIndex = columnKeys.length - 1;
    if (i == lastIndex) return true; // show the last column
    if (i > lastIndex - minColumnWidthIndex) return false; // show the last columns
    return (i + 1) % minColumnWidthIndex === 0;
  });
  const displayedColumnKeysSet = new Set(displayedColumnKeys);
  const condensed = columnKeys.length > displayedColumnKeys.length;

  const generateXAxis = axisBottom(scaleX)
    .tickSizeOuter(0)
    .tickSizeInner(10)
    .tickPadding(-3)
    .tickFormat((columnId) => {
      if (!displayedColumnKeysSet.has(columnId)) {
        return "";
      }
      if (columns?.has(columnId)) {
        return getColumnTitle(columnId);
      }
      return columnId;
    });

  // Create x axis
  const gX = g.select<SVGGElement>(".gx");

  gX.classed("-condensed", condensed);

  const gXTransition = gX
    .transition()
    .duration(chart.animationDuration)
    .call(generateXAxis);
  
  if (props.settings.xAxisLabelsVisible) {
    gXTransition.call(generateXAxis);

    // Rotate X labels if the text is too long.
    if (labelsRotated.value) {
      gX.selectAll<SVGTextElement, string>(".tick > text")
        .attr("transform", "translate(-10,0)rotate(-45)")
        .style("text-anchor", "end");
    } else {
      // un-rotate the labels
      gX.selectAll<SVGTextElement, string>(".tick > text")
        .attr("transform", "translate(0,0)rotate(0)")
        .style("text-anchor", "middle");
    }

    gX.selectAll<SVGTextElement, string>(".tick > text")
      .attr("font-size", props.settings.fontSize || 12);

    // Reposition the ticks
    gXTransition
      .selectAll<SVGGElement, string>(".tick")

      // hide the first column
      // (the start column is only used for the previous value)
      .style("opacity", (d) => {
        if (!columns) return 0;
        if (!columnKeys.includes(d)) return 0;
        if (!doesColumnHaveData(d)) return 0.5;
        return 1;
      })

      // Move all ticks to the right, to fill the space left by the first column
      .attr("transform", (d, i, els) => {
        if (!columns) return els[i].getAttribute("transform");
        const column = columns.get(d);
        if (!column) return els[i].getAttribute("transform");
        const index = columnKeys.indexOf(d);
        return `translate(${
          columnWidth * (index + 0.5) + xScale.value.padding() * columnWidth
        })`;
      });
  } else {
    console.log("settings", {...props.settings});
    gX.selectAll(".tick").remove();
  }

  gXTransition.attr(
    "transform",
    `translate(0, ${chart.innerHeight + roundEntriesHeight.value + 3 })`
  );

  // Add round effect markers below the x axis
  if (props.settings.roundEffectsVisible) {
    addRoundActionSymbols(
      g,
      props.columnData,
      (event, position, entry) => {
        emit("open-round-actions", {
          entry,
          position
        });
      },
      {
        animationDuration: chart.animationDuration,
        columnWidth,
        height: roundEntriesHeight.value
      }
    );
  } else {
    g.selectAll(".gx .chart-roundactions").remove();
    g.selectAll(".gx .roundactions-simsymbol").remove();
  }
}

function doesColumnHaveData(columnId: string): boolean {
  for (const group of props.data) {
    for (const entry of group.values) {
      if (entry.columnId === columnId && entry.value != null) {
        return true;
      }
    }
  }
  return false;
}

function drawYAxisLabels() {
  if (!props.settings.yAxisTitleVisible) {
    chart.container?.selectAll(".axis-label--y").remove();
    return;
  }
  const g = chart.container;
  if (!g) return;

  const data = chart.yUnits.map((unit) => ({
    unit,
    color: chart.unitColors[unit],
    key: props.settings.yAxis + unit
  }));

  const fontSize = props.settings.fontSize || 12;
  const gapsWidth = fontSize * 2;

  g.selectAll(".axis-label--y")
    .data(data, (d) => (d as { key: string }).key)
    .join(
      (enter) => {
        const group = enter
          .append("g")
          .attr("class", "axis-label--y")
          .on("click", (event) => {
            emit("click:axis-title", {
              axis: "y",
              x: event.clientX,
              y: event.clientY
            });
          })
          .attr("transform", (d) => {
            const i = chart.yUnits.indexOf(d.unit);
            const unitsBefore = chart.yUnits.slice(i + 1);
            const barX = unitsBefore.reduce(
              (sum, u) => (chart.yUnitWidths?.[u] ?? (fontSize * 4)) + sum,
              0
            );
            const gapOffset = Math.max(0, (chart.yUnits.length - 1 - i) * gapsWidth);

            return `translate(${-barX - gapOffset}, 0)`;
          });

        // shadow
        group
          .append("text")
          .attr("class", "axis-label__shadow")
          .attr("x", -10)
          .attr("y", 2)
          .attr("dy", "1em")
          .attr("text-anchor", "end")
          .attr("transform", "rotate(-90)")
          .attr("font-size", fontSize)
          .attr("stroke", "var(--color-background-deep)")
          .attr("stroke-width", 4)
          .attr("stroke-linejoin", "round")
          .attr("stroke-linecap", "round")
          .style("opacity", 0.7)
          .text((d) => {
            const text = props.settings.yAxis
              ? t(`chartAxes.${props.settings.yAxis}`)
              : "";
            return text + (d.unit.trim() ? ` [ ${d.unit} ]` : "");
          });

        // text
        group
          .append("text")
          .attr("class", "axis-label__text")
          .attr("x", -10)
          .attr("y", 2)
          .attr("dy", "1em")
          .attr("text-anchor", "end")
          .attr("transform", "rotate(-90)")
          .attr("font-size", fontSize)
          .attr("fill", (d) => d.color ?? "var(--color-text)")
          .text((d) => {
            const text = props.settings.yAxis
              ? t(`chartAxes.${props.settings.yAxis}`)
              : "";
            return text + (d.unit.trim() ? ` [ ${d.unit} ]` : "");
          });

        return group;
      },
      (update) => {
        update
          .transition()
          .duration(chart.animationDuration)
          .attr("transform", (d) => {
            const i = chart.yUnits.indexOf(d.unit);
            const unitsBefore = chart.yUnits.slice(i + 1);
            const barX = unitsBefore.reduce(
              (sum, u) => (chart.yUnitWidths?.[u] ?? (fontSize * 4)) + sum,
              0
            );
            const gapOffset = Math.max(0, (chart.yUnits.length - 1 - i) * gapsWidth);

            return `translate(${-barX - gapOffset}, 0)`;
          });

        update
          .select(".axis-label__text")
          .text((d) => {
            const text = props.settings.yAxis
              ? t(`chartAxes.${props.settings.yAxis}`)
              : "";
            return text + (d.unit.trim() ? ` [ ${d.unit} ]` : "");
          })
          .attr("fill", (d) => d.color ?? "var(--color-text)")
          .attr("font-size", fontSize);

        update.select(".axis-label__shadow").text((d) => {
          const text = props.settings.yAxis
            ? t(`chartAxes.${props.settings.yAxis}`)
            : "";
          return text + (d.unit.trim() ? ` [ ${d.unit} ]` : "");
        });

        return update;
      },
      (exit) => exit.remove()
    );
}

function drawXAxisLabel() {
  if (!props.settings.xAxisTitleVisible) {
    chart.container?.select(".axis-label--x").remove();
    return;
  }
  const g = chart.container;
  if (!g) return;

  // draw x axis label
  const xAxisLabelString = props.settings.xAxis
    ? t(`chartAxes.${props.settings.xAxis}`)
    : "";

  if (g.select(".axis-label--x").empty()) {
    g.append("g")
      .attr("class", "axis-label--x")
      .attr(
        "transform",
        `translate(${chart.innerWidth - OUTER_PADDING.value + 15}, ${
          chart.innerHeight
        })`
      )
      .on("click", (event) => {
        emit("click:axis-title", {
          axis: "x",
          x: event.clientX,
          y: event.clientY
        });
      });
  }

  const xAxisLabel = g.select(".axis-label--x");

  xAxisLabel
    .transition()
    .duration(chart.animationDuration)
    .attr(
      "transform",
      `translate(${chart.innerWidth - OUTER_PADDING.value + 15}, ${
        chart.innerHeight
      })`
    );

  // shadow
  selectOrCreateChild(xAxisLabel, "text.axis-label__shadow")
    .attr("x", 0)
    .attr("y", 5)
    .attr("dy", "1em")
    .attr("text-anchor", "start")
    .attr("font-size", props.settings.fontSize || 12)
    .attr("stroke", "var(--color-background-deep)")
    .attr("stroke-width", 4)
    .attr("stroke-linejoin", "round")
    .attr("stroke-linecap", "round")
    .style("opacity", 0.7)
    .text(xAxisLabelString);

  // text
  selectOrCreateChild(xAxisLabel, "text.axis-label__text")
    .attr("x", 0)
    .attr("y", 5)
    .attr("dy", "1em")
    .attr("text-anchor", "start")
    .attr("font-size", props.settings.fontSize || 12)
    .attr("fill", "var(--color-text)")
    .text(xAxisLabelString);

  const width = chart.measureTextWidthCached(xAxisLabelString, props.settings.fontSize || 12);

  selectOrCreateChild(xAxisLabel, "path")
    .attr(
      "d",
      `M0,0.5 L${width + 5},0.5 L${width + 2},-2 L${width + 5},0.5 L${
        width + 2
      },3`
    )
    .attr("stroke", "var(--color-text-light)")
    .attr("fill-opacity", "0.0");
}

function drawScaleColors() {
  if (!chart.container) return;

  const percentUnitIndex = chart.yUnits.indexOf("%");
  const colorScale = drawColorScale(chart.container, yScaleByUnit.value["%"], {
    height: chart.innerHeight,
    id: props.id,
    scaleColors: props.settings.scaleColors,
    scaleColorsVisible:
      props.settings.scaleColorsVisible && percentUnitIndex >= 0,
    animationDuration: chart.animationDuration
  });
  if (percentUnitIndex >= 0 && colorScale) {
    if (props.settings.yAxisLabelsVisible) {
      const i = percentUnitIndex;
      const unitsBefore = chart.yUnits.slice(i + 1);
      const barX = unitsBefore.reduce(
        (sum, u) => (chart.yUnitWidths?.[u] ?? 50) + sum,
        0
      );
      const gapOffset = Math.max(0, (chart.yUnits.length - 1 - i) * 25);

      colorScale
        .transition()
        .duration(chart.animationDuration)
        .attr("transform", `translate(${-barX - gapOffset}, 0)`);
    } else {
      colorScale
        .transition()
        .duration(chart.animationDuration)
        .attr("transform", `translate(0, 0)`);
    }
  }
}

/**
 * Update and draw all lines.
 */
function drawLines() {
  const scaleX = xScale.value;
  const g = chart.container;

  if (!g) return;

  const linePathGenerator = line<EntryValue>()
    .defined(d => d.value != null && !isNaN(d.value) && d.value != Infinity && d.value != -Infinity)
    .x((d) => scaleX(d.columnId) ?? 0)
    .y((d) => getY(d.unit, d.value));

  // Create the main lines
  const lines = g
    .select(".chart-groups")
    .selectAll<SVGGElement, DataEntry>(".chart-group")
    .data(chart.filteredData, (d) => d.key)
    .join(
      (enter) => {
        // create the line group
        const g = enter.append("g").attr("class", "chart-group");

        // create line path
        g.append("path")
          .attr("class", "line-chart__path")
          .attr("d", (d) => linePathGenerator(d.values));

        return g;
      },
      (update) => {
        // Update line paths:
        update
          .select(".line-chart__path")
          .transition()
          .duration(chart.animationDuration)
          .attr("d", (d) => linePathGenerator(d.values));

        return update;
      },
      (exit) => exit.remove()
    );

  // update the style of each line:
  lines
    .classed("-filled", (d) => !!d.filled)
    .attr("style", (d) => {
      // Es darf nur ein style attr geben!
      let style = "";

      if (d.color) {
        style += `--color: ${d.color};`;
      }
      if (d.filled === true) {
        if (d.color) {
          style += `--filled: ${d.color}`;
        } else {
          style += `--filled: var(--color-text)`;
        }
      }

      return style;
    })
    .on("mouseenter", function () {
      const group = select(this);
      group.raise();
      if (props.settings.endPointsVisible) return;
      group.select(".line-chart__path").classed("-hovered", true);
    })
    .on("mouseleave", function () {
      const group = select(this);
      group.select(".line-chart__path").classed("-hovered", false);
      chart.closeHoverBox();
    })
    .on("mousemove", function (event: MouseEvent, dataEntry) {
      if (props.settings.endPointsVisible) return;

      const localMousePosition = chart.getLocalPosition({
        x: event.clientX ?? 0,
        y: event.clientY ?? 0
      }, this);

      const closestValue = dataEntry.values.reduce<
        EntryValue | null
      >((closest, current) => {
        if (closest == null) return current;
        const closestX = scaleX(closest.columnId) ?? 0;
        const currentX = scaleX(current.columnId) ?? 0;
        if (
          Math.abs(localMousePosition.x - currentX) <
          Math.abs(localMousePosition.x - closestX)
        ) {
          return current;
        }
        return closest;
      }, null);

      if (!closestValue) {
        chart.closeHoverBox();
        return;
      }

      chart.updateHoverBox({
        x: event.clientX,
        y: event.clientY
      }, closestValue!, dataEntry);
    });

  // draw lines and labels
  drawLineLabels(lines);
  drawLineSymbols(lines);
}

function drawLineLabels<T extends SVGElement, P extends BaseType>(
  lines: Selection<T, DataEntry, P, unknown>
) {
  const scaleX = xScale.value;
  const g = chart.container;

  if (!g) return;
  if (chart.filteredData.length < 1) return;

  if (!props.settings.groupLabelsVisible) {
    lines.select(".line-chart__label").remove();
    lines.select(".chart-label-connect-line").remove();
    return;
  }

  // Get the X end position of the longest line.
  // All labels should be aligned after that end.
  const longestLineEndX = scaleX(
    chart.longestValuesArray[chart.longestValuesArray.length - 1]?.columnId
  );

  const labelYPositions = new Map<string | number, number>();
  for (const d of chart.filteredData) {
    const notNullValues = d.values.filter(d => d.value != null);
    if (notNullValues.length === 0) continue;
    const lastEntryValue = notNullValues[notNullValues.length - 1];
    if (!lastEntryValue) continue;
    labelYPositions.set(
      d.key,
      getY(lastEntryValue.unit, lastEntryValue?.value || 0) + 4
    );
  }

  const { positions: spacedLabelYPositions, moved } =
    getVerticallySpacedLabelYs(
      labelYPositions,
      props.margin.top,
      props.margin.top + chart.innerHeight - 7,
      (props.settings.fontSize || 12) * 1.2
    );

  const labelsX = (longestLineEndX ?? 0) + 15;
  const fontSize = props.settings.fontSize || 12;

  // Update line labels:
  const W = 15;
  const labels = lines
    .selectAll<SVGTextElement, DataEntry>(".line-chart__label")
    .data((d) => [d])
    .join(
      (enter) =>
        enter
          .append("text")
          .attr("class", "line-chart__label")
          .attr("x", moved ? labelsX + W : labelsX)
          .attr("y", (d) => spacedLabelYPositions.get(d.key) || 0)
          .attr("font-size", fontSize + 2),
      (update) => update,
      (exit) => exit.remove()
    )
    .text(getLabelTitle)
    .on("dblclick", (_event, d) => {
      emit("click:column-title", {
        uid: d.key,
        title: d.title,
        shortTitle: d.shortTitle,
        groupTitle: d.groupTitle,
        groupShortTitle: d.groupShortTitle,
        type: d.type,
        id: d.id,
        withSimulation: d.groupTitle != null
      });
    });

  labels
    .transition()
    .duration(chart.animationDuration)
    .attr("y", (d) => spacedLabelYPositions.get(d.key) || 0)
    .attr("x", moved ? labelsX + W : labelsX)
    .attr("font-size", fontSize + 2);

  labels
    .selectAll("title")
    .data((d) => [d])
    .join("title")
    .text((d) => d.title);

  if (moved) {
    lines
      .selectAll(".chart-label-connect-line")
      .data((d) => [d])
      .join("line")
      .attr("class", "chart-label-connect-line")
      .attr("x1", labelsX - 8)
      .attr("x2", labelsX + W - 3)
      .transition()
      .duration(chart.animationDuration)
      .attr(
        "y1",
        (d) => {
          const notNullValues = d.values.filter(d => d.value != null);
          if (notNullValues.length === 0) return chart.innerHeight;
          return getY(d.values[0]?.unit, notNullValues[notNullValues.length - 1]?.value || 0) + 1
        }
      )
      .attr("y2", (d) => (spacedLabelYPositions.get(d.key) || 0) - 3);
  } else {
    lines.selectAll(".chart-label-connect-line").remove();
  }
}

function getY(unit: string, value: number | null | undefined) {
  if (value == null || isNaN(value)) return chart.innerHeight;
  if (value == Infinity) value = Number.MAX_SAFE_INTEGER;
  if (value == -Infinity) value = Number.MIN_SAFE_INTEGER;
  const yScale = yScaleByUnit.value[unit];
  if (!yScale) {
    captureException(
      new Error(`No y scale found for unit "${unit}" in LineChart.`),
      { contexts: { LineChart: { yScaleByUnitKeys: Object.keys(yScaleByUnit.value).join(", ") } } }
    );
    return 0;
  }
  return yScale(value);
}

/**
 * Create all symbols of the chart.
 * @param lines D3 selection for all line groups of the chart.
 */
function drawLineSymbols<T extends SVGElement, P extends BaseType>(
  lines: Selection<T, DataEntry, P, unknown>
) {
  // Create all line symbols:
  const symbols = lines
    .selectAll<SVGElement, EntryValue>(".chart__symbol")
    .data(
      (d) => {
        if (!props.settings.endPointsVisible) return [];
        const values = d.values.filter((v) => v.value != null).map((v) => ({
          ...v,
          symbol: d.symbol ?? "circle",
          parentItem: d
        }));

        return values;
      },
      (d) => d.key
    )
    .join(
      (enter) => {
        return chart
          .appendSymbol(enter)
          .attr(
            "transform",
            (d) =>
              `translate(${xScale.value(d.columnId)}, ${getY(d.unit, d.value)})`
          );
      },
      (update) =>
        update
          .transition()
          .duration(chart.animationDuration)
          .attr(
            "transform",
            (d) =>
              `translate(${xScale.value(d.columnId)}, ${getY(d.unit, d.value)})`
          ),
      (exit) => exit.remove()
    );

  chart.updateSymbol(symbols);
}

function getLabelTitle(d: DataEntry) {
  let title = props.settings.groupLabelsShortened ? d.shortTitle : d.title;

  // add group title to the label
  if (props.settings.groupLabelsShortened && d.groupShortTitle) {
    title = title + " (" + d.groupShortTitle + ")";
  } else if (!props.settings.groupLabelsShortened && d.groupTitle) {
    title = title + " (" + d.groupTitle + ")";
  }

  if (d.values.length > 0 && d.values[0].unit) {
    title += " " + t("labels.inUnit", { unit: d.values[0].unit });
  }

  return title;
}
</script>

<style lang="scss">
@use "./chart.scss";

.line-chart {
  .chart-label-connect-line {
    stroke: var(--color, var(--color-grey-darker));
    stroke-dasharray: 3px 4px;
    opacity: 0.6;
  }

  &__path {
    fill: none;

    stroke: var(--color, var(--color-text));
    stroke-width: 1;
    stroke-linejoin: round;
    transition: stroke-width 100ms ease;

    &.-hovered {
      stroke-width: 2;
    }
  }
  &__label {
    fill: var(--color, var(--color-text));
    text-decoration: none;
  }
}
</style>
